import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Grid,
  Divider,
  Skeleton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Card from "../../../components/ui/Card";
import { useSelector } from "react-redux";
import { RootState } from "../../../services/redux/store";
import queryString from "query-string";
import service from "../../../services/api/service"; // API service
import { formatDate } from "../../../utils/dateFormatUtils";
import {
  formatIndianRupees,
  formatNormalIndianRupees,
} from "../../../utils/formatting";
import postsServices from "../../../services/api/service/postsService";

interface Item {
  accountNumber: string;
  name: string;
  status: string;
  initiatedBy: string;
  requested: string;
  disbursed: string;
  amount: string;
  date: string;
  party: string;
  utrNumber: string;
}

const maskString = (str: string, start: number, end: number) => {
  // Ensure start and end values are non-negative
  if (start < 0 || end < 0) {
    throw new RangeError("Start and end values must be non-negative.");
  }

  // Calculate the mask length
  const maskLength = str.length - start - end;

  // Ensure the mask length is non-negative
  if (maskLength < 0) {
    return str; // Return the original string if mask length is invalid
  }

  // Apply the mask
  return (
    str.substring(0, start) +
    "*".repeat(maskLength) +
    str.substring(str.length - end)
  );
};

const ItemCard = ({ item, isLoading }: { item: Item; isLoading: boolean }) => (
  <Box mb={2}>
    <Card>
      <Grid
        container
        className="dashboard-container-mobile"
        style={{ marginBottom: "5px" }}
      >
        <Grid item xs={5} sm={5}>
          <Typography className="insidePageLableMobile">
            Account Number
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width={100} />
          ) : (
            <Typography className="insidePageValueItemMobile">
              {item.accountNumber}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} sm={4}>
          {isLoading ? (
            <Skeleton variant="text" width={100} />
          ) : (
            <>
              <span
                className="insidePageLableMobile"
                style={{ marginRight: "5px" }}
              >
                Date
              </span>
              <span className="insidePageValueItemMobile">
                {formatDate(item.date)}
              </span>
            </>
          )}
        </Grid>
        <Grid item xs={3} sm={3}>
          <Typography className="insidePageLableMobile">
            Beneficiary Party
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width={100} />
          ) : (
            <Typography className="insidePageValueItemMobile">
              {item.party}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Divider />
      <Grid
        container
        className="dashboard-container-mobile"
        style={{ marginBottom: "5px" }}
      >
        <Grid item xs={5} sm={5}>
          <Typography className="insidePageLableMobile">
            Beneficiary Name
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width={100} />
          ) : (
            <Typography className="insidePageValueItemMobile">
              {item.name}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} sm={4}>
          <Typography className="insidePageLableMobile">Amount</Typography>
          {isLoading ? (
            <Skeleton variant="text" width={100} />
          ) : (
            <Typography
              className="insidePageValueItemMobile"
              style={{ color: "#28c76f" }}
            >
              {formatIndianRupees(Number(item?.amount))}
            </Typography>
          )}
        </Grid>
        <Grid item xs={3} sm={3}>
          <Typography className="insidePageLableMobile">UTR</Typography>
          {isLoading ? (
            <Skeleton variant="text" width={100} />
          ) : (
            <Typography className="insidePageValueItemMobile">
              {item.utrNumber}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Card>
  </Box>
);

const CardItemListDashboard = () => {
  const [cardListRows, setCardListRows] = useState<Item[]>([]);
  const [responseReceived, setResponseReceived] = useState(false);
  const selectedLoanNumber = useSelector(
    (state: RootState) => state.dashboardData.selectedLoanNumber
  );

  useEffect(() => {
    const fetchData = async () => {
      setResponseReceived(false);
      setCardListRows([]); // Clear previous data
      const payload = { lan: selectedLoanNumber };
      try {
        const response = await postsServices.addPost(
          "api/v1/dashboard/disbursement-history",
          payload
        );
        setCardListRows(response?.data?.data.slice(0, 5) || []);
      } catch (error) {
        console.error(error);
      } finally {
        setResponseReceived(true);
      }
    };

    if (selectedLoanNumber) {
      fetchData();
    }
  }, [selectedLoanNumber]);

  return (
    <div>
      {responseReceived ? (
        cardListRows.length === 0 ? (
          <Typography variant="h6" align="center" className="no-records-found">
            No records found
          </Typography>
        ) : (
          cardListRows.map((item) => (
            <ItemCard key={item.accountNumber} item={item} isLoading={false} />
          ))
        )
      ) : (
        <Typography variant="h6" align="center" className="no-records-found">
          Loading...
        </Typography>
        // <Skeleton variant="rectangular" width="100%" height={118} />
      )}
    </div>
  );
};

export default CardItemListDashboard;
